const CLOUDINARY_SCRIPT_ID = "cloudinary-upload-widget";
const CLOUDINARY_SCRIPT_SRC =
  "https://upload-widget.cloudinary.com/latest/global/all.js";

/**
 * Ensures window.cloudinary is available (loads script if needed), then runs callback.
 * Never throws if Cloudinary is blocked/unavailable — callback simply won't run.
 */
export function whenCloudinaryReady(callback) {
  if (typeof window === "undefined") return;

  if (window.cloudinary?.createUploadWidget) {
    callback(window.cloudinary);
    return;
  }

  const existing = document.getElementById(CLOUDINARY_SCRIPT_ID);
  const onReady = () => {
    if (window.cloudinary?.createUploadWidget) {
      callback(window.cloudinary);
    } else {
      console.warn("Cloudinary upload widget failed to load.");
    }
  };

  if (existing) {
    if (window.cloudinary?.createUploadWidget) {
      onReady();
    } else {
      existing.addEventListener("load", onReady, { once: true });
    }
    return;
  }

  // Fall back: script in index.html may still be loading under a different id
  const indexScript = document.querySelector(
    `script[src*="upload-widget.cloudinary.com"]`
  );
  if (indexScript && !window.cloudinary?.createUploadWidget) {
    indexScript.addEventListener("load", onReady, { once: true });
    // Also retry shortly in case load already fired
    setTimeout(onReady, 500);
    return;
  }

  const script = document.createElement("script");
  script.id = CLOUDINARY_SCRIPT_ID;
  script.src = CLOUDINARY_SCRIPT_SRC;
  script.async = true;
  script.addEventListener("load", onReady, { once: true });
  script.addEventListener("error", () => {
    console.warn("Cloudinary upload widget script could not be loaded.");
  });
  document.body.appendChild(script);
}

/**
 * Safely create a Cloudinary upload widget. Returns destroy/cleanup function.
 */
export function createSafeUploadWidget(config, resultCallback) {
  let widget = null;
  let cancelled = false;

  whenCloudinaryReady((cloudinary) => {
    if (cancelled) return;
    try {
      widget = cloudinary.createUploadWidget(config, resultCallback);
    } catch (err) {
      console.warn("Failed to create Cloudinary upload widget:", err);
    }
  });

  return {
    open: () => widget?.open?.(),
    destroy: () => {
      cancelled = true;
      try {
        widget?.destroy?.();
      } catch {
        /* ignore */
      }
      widget = null;
    },
    getWidget: () => widget,
  };
}
